import apiClient, { apiService } from './api';
import { PaginationParams, PrintOutputType, PrintResult } from '../types';

// ─── Status types ──────────────────────────────────────────────────────────────

export type SalesInvoiceStatus =
  | 'Draft'
  | 'Issued'
  | 'PartiallyPaid'
  | 'Paid'
  | 'Overdue'
  | 'Voided'
  | 'Cancelled';

export type PaymentStatus = 'Unpaid' | 'PartiallyPaid' | 'Paid' | 'Voided';

// ─── DTOs ──────────────────────────────────────────────────────────────────────

export interface SalesInvoiceLineDTO {
  id: number;
  salesInvoiceId: number;
  lineType: 'Product' | 'Service';
  salesItemId?: number;
  productId?: number | null;
  serviceItemId?: number | null;
  productName?: string | null;
  productSKU?: string | null;
  description?: string | null;
  quantity: number;
  unitPrice: number;
  discountAmount: number;
  taxRate: number;
  taxAmount: number;
  lineTotal: number;
}

export interface SalesInvoiceListDTO {
  id: number;
  invoiceNumber: string;
  customerId: number;
  customerName: string;
  invoiceDate: string;
  dueDate: string;
  status: SalesInvoiceStatus;
  paymentStatus: PaymentStatus;
  subtotal: number;
  taxAmount: number;
  discountAmount: number;
  totalAmount: number;
  amountPaid: number;
  balanceDue: number;
  lineCount: number;
  isCredit: boolean;
  isOverdue: boolean;
  creditPeriodDays: number;
  referenceNumber?: string | null;
}

export interface SalesInvoiceDTO extends SalesInvoiceListDTO {
  notes?: string;
  lines: SalesInvoiceLineDTO[];
  createdAt: string;
  updatedAt?: string;

  /** Populated once the invoice has been voided. */
  voidReason?: string | null;
  voidedAt?: string | null;
  voidedByName?: string | null;
}

// ─── Requests ──────────────────────────────────────────────────────────────────

export interface CreateSalesInvoiceLineRequest {
  lineType?: 'Product' | 'Service';
  salesItemId?: number;
  productId?: number | null;
  serviceItemId?: number | null;
  description?: string;
  quantity: number;
  unitPrice: number;
  discountAmount?: number;
  taxRate?: number;
}

export interface CreateSalesInvoiceRequest {
  customerId: number;
  invoiceDate: string;
  dueDate: string;
  notes?: string;
  referenceNumber?: string;
  /** true = credit sale (appears in Outstanding Invoices until settled) */
  isCredit: boolean;
  lines: CreateSalesInvoiceLineRequest[];
}

export interface CreateAndPaySalesInvoiceRequest extends CreateSalesInvoiceRequest {
  amountPaid: number;
  tenderedAmount?: number;
  paymentMethod: string;
  paymentReferenceNumber?: string;
  paymentNotes?: string;
}

export interface UpdateSalesInvoiceRequest {
  invoiceDate: string;
  dueDate: string;
  notes?: string;
  referenceNumber?: string;
  lines: CreateSalesInvoiceLineRequest[];
}

export interface RecordPaymentRequest {
  amountPaid: number;
  tenderedAmount?: number;
  paymentDate: string;
  paymentMethod?: string;
  referenceNumber?: string;
  notes?: string;
}

export interface SettleCreditInvoiceRequest {
  settlementDate: string;
  remarks?: string;
}

// ─── Outstanding summary ───────────────────────────────────────────────────────

export interface OutstandingInvoiceSummaryDTO {
  totalCount:        number;
  overdueCount:      number;
  pendingCount:      number;
  totalBalanceDue:   number;
  overdueBalanceDue: number;
  pendingBalanceDue: number;
}

// ─── By-product report DTOs ────────────────────────────────────────────────────

export interface ProductInvoiceSummaryDTO {
  invoiceId:     number;
  invoiceNumber: string;
  customerName:  string | null;
  invoiceDate:   string;
  quantity:      number;
  lineTotal:     number;
  status:        string;
  paymentStatus: string;
}

export interface ProductSalesDTO {
  productId:        number;
  productName:      string;
  productSKU:       string;
  totalQuantity:    number;
  totalRevenue:     number;
  numberOfInvoices: number;
  averagePrice:     number;
  invoices:         ProductInvoiceSummaryDTO[];
}

// ─── Restaurant-order counterparts — a company may sell through invoicing or
// restaurant orders (or both), so Search/By-Product report both channels ──────

export interface RestaurantOrderSummaryDTO {
  id:             number;
  orderNumber:    string;
  customerName:   string | null;
  orderDate:      string;
  status:         string;
  subtotal:       number;
  taxAmount:      number;
  discountAmount: number;
  totalAmount:    number;
  amountPaid:     number;
  balanceDue:     number;
  lineCount:      number;
}

export interface SalesSearchResultDTO {
  invoices:         SalesInvoiceListDTO[];
  restaurantOrders: RestaurantOrderSummaryDTO[];
}

export interface MenuItemOrderSummaryDTO {
  orderId:      number;
  orderNumber:  string;
  customerName: string | null;
  orderDate:    string;
  quantity:     number;
  lineTotal:    number;
  status:       string;
}

export interface MenuItemSalesDTO {
  menuItemId:     number;
  menuItemName:   string;
  totalQuantity:  number;
  totalRevenue:   number;
  numberOfOrders: number;
  averagePrice:   number;
  orders:         MenuItemOrderSummaryDTO[];
}

export interface SalesByProductResultDTO {
  products:  ProductSalesDTO[];
  menuItems: MenuItemSalesDTO[];
}

// ─── Payment record ────────────────────────────────────────────────────────────

export interface SalesInvoicePaymentDTO {
  id: number;
  salesInvoiceId: number;
  amountPaid: number;
  tenderedAmount?: number;
  changeGiven?: number;
  paymentDate: string;
  paymentMethod: string;
  referenceNumber?: string;
  notes?: string;
  recordedByUserId: number;
  recordedByUserName?: string;
  createdAt: string;
}

// ─── Service ───────────────────────────────────────────────────────────────────

const SI_API = '/sales-invoices';

export const salesInvoiceService = {
  getSalesInvoices: (params?: PaginationParams) =>
    apiService.get<SalesInvoiceListDTO[]>(
      SI_API,
      params as Record<string, unknown>
    ),

  getSalesInvoiceById: (id: number | string) =>
    apiService.get<SalesInvoiceDTO>(`${SI_API}/${id}`),

  createSalesInvoice: (data: CreateSalesInvoiceRequest) =>
    apiService.post<SalesInvoiceDTO>(SI_API, data),

  createAndPaySalesInvoice: (data: CreateAndPaySalesInvoiceRequest) =>
    apiService.post<SalesInvoiceDTO>(`${SI_API}/create-and-pay`, data),

  updateSalesInvoice: (id: number | string, data: UpdateSalesInvoiceRequest) =>
    apiService.put<SalesInvoiceDTO>(`${SI_API}/${id}`, data),

  deleteSalesInvoice: (id: number | string) =>
    apiService.delete<boolean>(`${SI_API}/${id}`),

  issueSalesInvoice: (id: number | string) =>
    apiService.post<SalesInvoiceDTO>(`${SI_API}/${id}/issue`, {}),

  cancelSalesInvoice: (id: number | string) =>
    apiService.post<SalesInvoiceDTO>(`${SI_API}/${id}/cancel`, {}),

  voidSalesInvoice: (id: number | string, reason: string) =>
    apiService.post<SalesInvoiceDTO>(`${SI_API}/${id}/void`, { reason }),

  recordPayment: (id: number | string, data: RecordPaymentRequest) =>
    apiService.post<SalesInvoiceDTO>(`${SI_API}/${id}/payments`, data),

  getPayments: (id: number | string) =>
    apiService.get<SalesInvoicePaymentDTO[]>(`${SI_API}/${id}/payments`),

  getOutstandingInvoices: (params?: PaginationParams & { customerId?: number }) =>
    apiService.get<SalesInvoiceListDTO[]>(
      `${SI_API}/outstanding`,
      params as Record<string, unknown>
    ),

  getOutstandingSummary: (customerId?: number) =>
    apiService.get<OutstandingInvoiceSummaryDTO>(
      `${SI_API}/outstanding/summary`,
      customerId ? ({ customerId } as Record<string, unknown>) : undefined
    ),

  settleCreditInvoice: (id: number | string, data: SettleCreditInvoiceRequest) =>
    apiService.post<SalesInvoiceDTO>(`${SI_API}/${id}/settle-credit`, data),

  printInvoice: (id: number | string, outputType?: PrintOutputType, printerProfileId?: number) => {
    const params = new URLSearchParams();
    if (outputType !== undefined) params.set('outputType', String(outputType));
    if (printerProfileId !== undefined) params.set('printerProfileId', String(printerProfileId));
    const query = params.toString();
    return apiService.post<PrintResult>(`${SI_API}/${id}/print${query ? `?${query}` : ''}`, {});
  },

  // ── PDF helpers (use apiClient to send JWT automatically) ─────────────────

  /**
   * Open an invoice PDF in a browser tab.
   *
   * IMPORTANT — call pattern to avoid popup blockers:
   *   const tab = window.open('', '_blank');          // open synchronously (user gesture)
   *   await salesInvoiceService.viewInvoicePdf(id, num, tab);  // inject URL async
   *
   * If `targetTab` is omitted the function falls back to window.open, but that
   * will be blocked by most browsers because it runs after an async await chain.
   */
  viewInvoicePdf: async (
    id: number | string,
    invoiceNumber: string,
    targetTab?: Window | null,
  ) => {
    const res = await apiClient.get<Blob>(`${SI_API}/${id}/pdf`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);

    if (targetTab && !targetTab.closed) {
      // Inject the blob URL into the already-opened tab (popup-blocker safe)
      targetTab.location.href = url;
    } else {
      // Fallback — may be blocked if called after an await
      window.open(url, '_blank');
    }

    // Revoke after the tab has had time to render the PDF
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },

  /** Download invoice PDF with COPY watermark. */
  downloadInvoicePdf: async (id: number | string, invoiceNumber: string) => {
    const res = await apiClient.get<Blob>(`${SI_API}/${id}/pdf?watermark=COPY`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);
    const a   = document.createElement('a');
    a.href     = url;
    a.download = `Invoice_${invoiceNumber}_COPY.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  },

  /** Download a reprint PDF — auto-assigns and stamps the next copy number. */
  reprintInvoicePdf: async (id: number | string, invoiceNumber: string) => {
    const res = await apiClient.get<Blob>(`${SI_API}/${id}/pdf?reprint=true`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);
    const a   = document.createElement('a');
    a.href     = url;
    a.download = `Invoice_${invoiceNumber}_COPY.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  },

  /** Inquiry search: find invoices by reference number or product SKU. */
  inquirySearch: (q: string) =>
    apiService.get<SalesInvoiceListDTO[]>(`${SI_API}/inquiry`, { q } as Record<string, unknown>),

  // ── Date-range search ──────────────────────────────────────────────────────

  searchByDateRange: (params: {
    startDate?: string; startTime?: string;
    endDate?:   string; endTime?:   string;
  }) => apiService.get<SalesSearchResultDTO>(`${SI_API}/search`, params as Record<string, unknown>),

  // ── By-product search + export ────────────────────────────────────────────

  searchByProduct: (params: {
    startDate?: string; startTime?: string;
    endDate?:   string; endTime?:   string;
  }) => apiService.get<SalesByProductResultDTO>(`${SI_API}/by-product`, params as Record<string, unknown>),

  exportProductCsv: async (params: {
    startDate?: string; startTime?: string;
    endDate?:   string; endTime?:   string;
  }, filename = 'sales-by-product.csv') => {
    const res = await apiClient.get<Blob>(`${SI_API}/by-product/export`, {
      params: { ...params, format: 'csv' },
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  },

  exportProductPdf: async (params: {
    startDate?: string; startTime?: string;
    endDate?:   string; endTime?:   string;
  }, filename = 'sales-by-product.pdf') => {
    const res = await apiClient.get<Blob>(`${SI_API}/by-product/export`, {
      params: { ...params, format: 'pdf' },
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  },

  // ── List exports ───────────────────────────────────────────────────────────

  /** Download the search result as a CSV file. */
  exportCsv: async (params: {
    startDate?: string; startTime?: string;
    endDate?:   string; endTime?:   string;
  }, filename = 'invoices.csv') => {
    const res = await apiClient.get<Blob>(`${SI_API}/export`, {
      params: { ...params, format: 'csv' },
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);
    const a   = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  },

  /** Download the search result as a formatted A4 PDF. */
  exportPdf: async (params: {
    startDate?: string; startTime?: string;
    endDate?:   string; endTime?:   string;
  }, filename = 'invoice-report.pdf') => {
    const res = await apiClient.get<Blob>(`${SI_API}/export`, {
      params: { ...params, format: 'pdf' },
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);
    const a   = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  },
};
